/**
 * One in-flight request per URL, shared by every `JobShareModal` on the page.
 *
 * A job ad mounts three of these and they all build the same URL, so without this the intent
 * handlers would fire three identical requests. The promise is cached, not the value, so a
 * second caller arriving mid-flight waits on the first instead of starting its own.
 *
 * A rejection is cached too, deliberately: the reason a call fails here is that the table is
 * throttled, and retrying on every hover would add load to the thing that is already over
 * capacity. The share still works, on the long URL.
 *
 * This lives beside the component rather than inside it because `ui-blocks/index.ts` re-exports
 * `./job-share-modal`, and anything exported from that file becomes part of the public
 * `@restworld/ui-ds` surface. `resetShortUrlRequests` is a test seam; app code calling it would
 * silently defeat the deduplication above.
 */

/** Enough for any realistic browsing session; see `remember` below for why there is a cap. */
const MAX_ENTRIES = 50;

const shortUrlRequests = new Map<string, Promise<string | null>>();

/**
 * Module state outlives every component, and client-side navigation between job ads never
 * resets it, so without a cap a long session would accumulate one entry per ad visited. The
 * oldest goes first: `Map` iterates in insertion order, and the ad a user is still likely to
 * share is the one they are looking at now.
 */
const remember = (url: string, request: Promise<string | null>) => {
  shortUrlRequests.set(url, request);

  if (shortUrlRequests.size > MAX_ENTRIES) {
    const oldest = shortUrlRequests.keys().next();
    if (!oldest.done) shortUrlRequests.delete(oldest.value);
  }
};

export const shortenOnce = (
  createShortenUrl: (params: { original_url: string }) => Promise<{ data?: { data?: { short_url?: string } } }>,
  url: string
): Promise<string | null> => {
  const inFlight = shortUrlRequests.get(url);
  if (inFlight) return inFlight;

  const request = createShortenUrl({ original_url: url })
    .then((response) => response?.data?.data?.short_url ?? null)
    .catch((error) => {
      console.error(`Error while creating short url: ${error}`);
      return null;
    });

  remember(url, request);
  return request;
};

/**
 * Test seam. The cache is module state, so it survives `render`/`cleanup` and would carry one
 * test's resolved (or rejected) request into the next. Not for production use, which is why it
 * is not reachable from the `@restworld/ui-ds` barrel.
 */
export const resetShortUrlRequests = () => shortUrlRequests.clear();
